import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTransition } from 'react-spring';
import { animated } from 'react-spring';
import { Root, Content, CloseButtonWrapper, ImageContainer } from './overlayStyles';
import { CloseIcon, Button } from '../../styleguide-remapped/styleguide-remapped';
import { cx } from '@linaria/core';
import NextImage from 'next/image';

export function ImageOverlay({ isVisible, onClose, image, alt, title, description }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return undefined;
    const opener = document.activeElement;
    closeRef.current?.querySelector('button')?.focus();
    return () => opener?.focus?.();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return undefined;
    const onKeyDown = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isVisible, onClose]);

  const transitions = useTransition(isVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return createPortal(
    transitions(
      (styles, item) =>
        item && (
          <animated.div
            className={cx(Root.base, 'image-overlay')}
            style={styles}
            role="dialog"
            aria-modal="true"
            aria-label={alt || 'Bildansicht'}
            aria-hidden={!isVisible}
          >
            <div className={cx(Content.base)} onClick={(event) => event.target === event.currentTarget && onClose()}>
              <div className={cx(CloseButtonWrapper.base)} ref={closeRef}>
                <Button
                  iconRight={<CloseIcon />}
                  isIconOnly={true}
                  onClick={onClose}
                  ariaLabel="Bild schließen"
                  text="Bild schließen"
                />
              </div>
              <div className={cx(ImageContainer.base)}>
                {image && (
                  <NextImage
                    src={image}
                    alt={alt || ''}
                    title={title || ''}
                    width={0}
                    height={0}
                    sizes="80vw"
                    style={{ width: '100%', height: 'auto', maxHeight: '90vh', minHeight: 0, objectFit: 'contain' }}
                  />
                )}
                {description && (
                  <p className="image-description">{description}</p>
                )}
              </div>
            </div>
          </animated.div>
        ),
    ),
    document.body,
  );
}

export default React.memo(ImageOverlay);
